import React from "react";
import { useAuth0 } from "@auth0/auth0-react";
import Button from "components/Button/Regular";
import vgToast from "utils/toast";
import {
  ProviderProfile,
  MAX_DESCRIPTION_LENGTH,
  emptyProfile,
  readProviderProfile,
  updateProviderProfile,
  validateProfile,
} from "services/provider-profile.service";

type Props = { orgId: number };

const KINDS: { key: "isFuneralHome" | "isCemetery" | "isCrematorium"; label: string; hint: string }[] = [
  { key: "isFuneralHome", label: "Funeral home", hint: "Arranges funerals and looks after families" },
  { key: "isCemetery", label: "Cemetery", hint: "Has grave plots to reserve or sell" },
  { key: "isCrematorium", label: "Crematorium", hint: "Has cremation and chapel times to book" },
];

/**
 * Which kinds of provider this organisation is, what the public sees, and whether it is
 * listed on the marketplace at all.
 */
const ProviderProfileForm = ({ orgId }: Props) => {
  const { getAccessTokenSilently } = useAuth0();
  const [profile, setProfile] = React.useState<ProviderProfile>(emptyProfile(orgId));
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const token = await getAccessTokenSilently();
      const { data, error: readError } = await readProviderProfile(token, orgId);
      if (cancelled) return;
      if (readError) setError(readError.message);
      else setProfile(data ?? emptyProfile(orgId));
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [orgId, getAccessTokenSilently]);

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    const invalid = validateProfile(profile);
    if (invalid) {
      setError(invalid);
      return;
    }
    setError(null);
    setSaving(true);
    const token = await getAccessTokenSilently();
    const { data, error: saveError } = await updateProviderProfile(token, profile);
    setSaving(false);
    if (saveError) {
      setError(saveError.message);
      return;
    }
    if (data) setProfile(data);
    vgToast.success("Profile saved.");
  };

  if (loading) return <p className="p-6 text-sm text-slate-500">Loading profile…</p>;

  return (
    <form onSubmit={save} className="p-6 flex flex-col gap-6">
      <fieldset className="flex flex-col gap-3">
        <legend className="text-slate-600 text-sm font-medium mb-1">What kind of provider are you?</legend>
        {KINDS.map(({ key, label, hint }) => (
          <label key={key} className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              className="mt-1 h-4 w-4"
              checked={profile[key]}
              onChange={(e) => setProfile({ ...profile, [key]: e.target.checked })}
            />
            <span>
              <span className="block text-sm font-medium text-slate-800">{label}</span>
              <span className="block text-xs text-slate-500">{hint}</span>
            </span>
          </label>
        ))}
      </fieldset>

      <div className="flex flex-col gap-y-1.5">
        <label htmlFor={`profile-description-${orgId}`} className="text-slate-600 text-sm font-medium">
          Public description
        </label>
        <textarea
          id={`profile-description-${orgId}`}
          rows={5}
          maxLength={MAX_DESCRIPTION_LENGTH}
          value={profile.publicDescription}
          onChange={(e) => setProfile({ ...profile, publicDescription: e.target.value })}
          placeholder="What families should know about you"
          className="rounded-md border border-slate-300 px-3 py-2 text-sm"
        />
        <span className="text-xs text-slate-400">
          {profile.publicDescription.length} / {MAX_DESCRIPTION_LENGTH}
        </span>
      </div>

      <label className="flex items-start gap-3 cursor-pointer">
        <input
          type="checkbox"
          className="mt-1 h-4 w-4"
          checked={profile.isListed}
          onChange={(e) => setProfile({ ...profile, isListed: e.target.checked })}
        />
        <span>
          <span className="block text-sm font-medium text-slate-800">Listed on the Funely marketplace</span>
          <span className="block text-xs text-slate-500">
            Families can find you and see your public page.
            {profile.isListed && (
              <>
                {" "}
                <a className="underline" href={`/providers/${orgId}`} target="_blank" rel="noreferrer">
                  View your public page
                </a>
              </>
            )}
          </span>
        </span>
      </label>

      {error && <div className="text-sm text-red-500">{error}</div>}

      <div>
        <Button color="Primary" type="submit" disabled={saving}>
          <span>{saving ? "Saving…" : "Save profile"}</span>
        </Button>
      </div>
    </form>
  );
};

export default ProviderProfileForm;
